import {
  buildDefaultMappingSelection,
  buildMappingSelectionFromSaved,
  normalizeWorksheetPreviewMetadata,
} from "@/lib/mappingRegions";
import { getCachedPipelinePreview } from "@/lib/pipelinePreviewCache";
import type { PipelineFileMeta } from "@/lib/pipelineTypes";
import { fetchMappingSnapshot, loadMappingSelection, previewExtraction } from "@/services/api";
import { resolveBatchSourceFile } from "@/services/batchFileCache";
import type { ExtractionPreviewData, KnownMapping, ParsedWorksheet, TargetSchema } from "@/types";

export interface HydrateDqxNavigationParams {
  fileId: string;
  batchFileMeta: PipelineFileMeta[];
  files: File[];
  file: File | null;
  schemas: TargetSchema[];
  /** Host's in-memory snapshots (files mapped this visit); server is the fallback. */
  savedMappingsByFileId?: Record<string, KnownMapping[]>;
  confirmUpload: (data: {
    file: File;
    fileName: string;
    documentId: string;
    schemaId: string;
    schemaName: string;
    worksheets: ParsedWorksheet[];
    mappingSelection: Record<string, boolean>;
    reportType?: string | null;
  }) => void;
  saveMappings: (mappings: KnownMapping[]) => void;
}

async function resolvePreviewForHydration(fileId: string): Promise<ExtractionPreviewData | null> {
  const cachedPreview = getCachedPipelinePreview(fileId);
  if (cachedPreview?.worksheets?.length) {
    return cachedPreview;
  }
  try {
    const preview = await previewExtraction(fileId);
    return preview?.worksheets?.length ? preview : null;
  } catch {
    // Artifacts may already be released (e.g. Published / clean DQ).
    return null;
  }
}

export interface DqxPreviewHydration {
  worksheets: ParsedWorksheet[];
  mappingSelection: Record<string, boolean>;
  savedMappings: KnownMapping[];
}

/**
 * Load extraction preview + mappings for DQ View after refresh.
 * Returns null when preview artifacts or the mapping snapshot are unavailable.
 */
export async function resolveDqxPreviewHydration(params: {
  fileId: string;
  savedMappings?: KnownMapping[] | null;
}): Promise<DqxPreviewHydration | null> {
  const preview = await resolvePreviewForHydration(params.fileId);
  if (!preview?.worksheets?.length) {
    return null;
  }

  let savedMappings = params.savedMappings?.length ? params.savedMappings : null;
  if (!savedMappings?.length) {
    try {
      savedMappings = await fetchMappingSnapshot(params.fileId);
    } catch {
      savedMappings = null;
    }
  }
  if (!savedMappings?.length) {
    return null;
  }

  const worksheets = preview.worksheets;
  const normalizedWorksheets = normalizeWorksheetPreviewMetadata(worksheets);
  let mappingSelection = buildDefaultMappingSelection(normalizedWorksheets);
  try {
    const savedRegions = await loadMappingSelection(params.fileId);
    mappingSelection = buildMappingSelectionFromSaved(normalizedWorksheets, savedRegions);
  } catch {
    // Keep default: mapping regions selected, non-mapping unchecked.
  }

  return { worksheets, mappingSelection, savedMappings };
}

/** Banner only when Results were reconstructed AND preview could not be loaded. */
export function shouldShowQuarantineReconstructBanner(
  fromQuarantine: boolean,
  previewHydrateFailed: boolean
): boolean {
  return fromQuarantine && previewHydrateFailed;
}

/** Hydrate upload context from in-memory + server state without re-running extraction. */
export async function hydrateDqxNavigationFromSession(
  params: HydrateDqxNavigationParams
): Promise<boolean> {
  const meta = params.batchFileMeta.find((entry) => entry.fileId === params.fileId);
  if (!meta?.schemaId) {
    return false;
  }

  const hydrated = await resolveDqxPreviewHydration({
    fileId: params.fileId,
    savedMappings: params.savedMappingsByFileId?.[params.fileId] ?? null,
  });
  if (!hydrated) {
    return false;
  }

  const sourceFile =
    (await resolveBatchSourceFile(
      params.fileId,
      params.files,
      params.file,
      params.batchFileMeta
    )) ?? new File([], meta.fileName);

  const schemaName =
    params.schemas.find((schema) => schema.id === meta.schemaId)?.name ?? meta.schemaId;

  params.confirmUpload({
    file: sourceFile,
    fileName: meta.fileName,
    documentId: params.fileId,
    schemaId: meta.schemaId,
    schemaName,
    worksheets: hydrated.worksheets,
    mappingSelection: hydrated.mappingSelection,
    reportType: meta.reportType ?? null,
  });
  params.saveMappings(hydrated.savedMappings);
  return true;
}
